import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

export interface DeviceInfo {
  deviceId: string;
  platform: string;
  language: string;
  timezone: string;
  ipAddress: string;
}

@Injectable({ providedIn: 'root' })
export class DeviceFingerprintService {
  
  constructor(private http: HttpClient) { }

  /**
   * Generate a unique device fingerprint
   * Combines multiple device characteristics to create a relatively stable identifier
   */
  async getDeviceFingerprint(): Promise<string> {
    const deviceInfo = await this.collectDeviceInfo();
    const jsonStr = JSON.stringify(deviceInfo);
    console.log('Device Info for Fingerprint:', deviceInfo);
    console.log('JSON String:', jsonStr);
    const hash = await this.hashString(jsonStr);
    console.log('Generated Device ID:', hash);
    return hash;
  }

  /**
   * Get detailed device information
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    return await this.collectDeviceInfo();
  }

  /**
   * Collect device characteristics using only universally supported browser properties
   * This ensures consistency across all browsers (Chrome, Firefox, Safari, Edge, etc.)
   */
  private async collectDeviceInfo(): Promise<DeviceInfo> {
    // Normalize timezone - convert deprecated names to their modern equivalents
    let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    timezone = this.normalizeTimezone(timezone);

    // Get client IP address
    const ipAddress = await this.getClientIpAddress();

    return {
      deviceId: '', // Will be populated with hash
      platform: navigator.platform,
      language: navigator.language,
      timezone: timezone,
      ipAddress: ipAddress
    };
  }

  /**
   * Normalize timezone names - convert deprecated names to modern equivalents
   */
  private normalizeTimezone(tz: string): string {
    // Map of deprecated timezone names to their modern equivalents
    const timezoneMap: { [key: string]: string } = {
      'Asia/Calcutta': 'Asia/Kolkata',
      'Asia/Katmandu': 'Asia/Kathmandu',
      'Etc/GMT+0': 'UTC',
      'Etc/GMT-0': 'UTC',
      'Etc/GMT0': 'UTC',
      'Etc/Greenwich': 'UTC',
      'Etc/UCT': 'UTC',
      'Etc/Universal': 'UTC',
      'Etc/Zulu': 'UTC'
    };
    return timezoneMap[tz] || tz;
  }

  /**
   * Create SHA-256 hash from string
   */
  private async hashString(str: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Error generating device fingerprint:', error);
      // Fallback to simple hash if crypto is not available
      return this.simpleHash(str);
    }
  }

  /**
   * Fallback simple hash function
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Store device ID in localStorage
   */
  storeDeviceId(deviceId: string): void {
    localStorage.setItem('lawtrax_device_id', deviceId);
  }

  /**
   * Retrieve stored device ID from localStorage
   */
  getStoredDeviceId(): string | null {
    return localStorage.getItem('lawtrax_device_id');
  }

  /**
   * Clear stored device ID
   */
  clearDeviceId(): void {
    localStorage.removeItem('lawtrax_device_id');
  }

/**
 * Get client IP address using ipify service
 */
async getClientIpAddress(): Promise<string> {
  try {
    const response = await this.http.get<{ ip: string }>('https://api.ipify.org?format=json').toPromise();
    return response?.ip || 'unknown';
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return 'unknown';
  }
}
}
